import React, { Fragment, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Media,
  Row,
} from "reactstrap";
import { Breadcrumbs, H5 } from "../../AbstractElements";
import FooterCard from "../../Components/Forms/FormControl/Common/FooterCard";
import { titleCase } from "../../utils/case";
import { toast } from "react-toastify";
import { createAddOn } from "../../api/addon.api";

const InventoryCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const chargedByOptions = [
    "hourly",
    "nightly",
    "daily",
    "monthly",
    "yearly",
    "flat_fee",
  ];

  const [form, setForm] = useState({
    name: "",
    description: "",
    category: "",
    inventoryType: "",
    totalStock: "",
    lowStockThreshold: "",
    remark: "",
    status: "active",
    photo: null,
    requiresPayment: false,
    pricingStructures: chargedByOptions.map((chargedBy) => ({
      chargedBy,
      amount: "",
    })),
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isPricingSelected = (chargedBy) => {
    const pricing = form.pricingStructures.find(
      (item) => item.chargedBy === chargedBy
    );

    return !!pricing?.amount;
  };

  const isPricingDisabled = (chargedBy) => {
    const flatSelected = isPricingSelected("flat_fee");
    const dailySelected = isPricingSelected("daily");
    const nightlySelected = isPricingSelected("nightly");

    // Flat rate means no other pricing method can apply
    if (flatSelected && chargedBy !== "flat_fee") {
      return true;
    }

    // Per day and per night are mutually exclusive with
    // each other and with flat rate
    if (
      dailySelected &&
      (chargedBy === "flat_fee" || chargedBy === "nightly")
    ) {
      return true;
    }

    if (
      nightlySelected &&
      (chargedBy === "flat_fee" || chargedBy === "daily")
    ) {
      return true;
    }

    return false;
  };

  const handlePricingChange = (chargedBy, value) => {
    setForm((prev) => {
      const hasValue = value !== "";

      // Flat rate cannot be combined with anything else
      if (chargedBy === "flat_fee" && hasValue) {
        return {
          ...prev,
          pricingStructures: prev.pricingStructures.map((item) => ({
            ...item,
            amount:
              item.chargedBy === "flat_fee"
                ? value
                : "",
          })),
        };
      }

      // If selecting another pricing method while flat rate exists,
      // remove flat rate.
      if (chargedBy !== "flat_fee" && hasValue) {
        return {
          ...prev,
          pricingStructures: prev.pricingStructures.map((item) => {
            if (item.chargedBy === "flat_fee") {
              return {
                ...item,
                amount: "",
              };
            }

            // Per Day and Per Night cannot coexist
            if (
              chargedBy === "daily" &&
              item.chargedBy === "nightly"
            ) {
              return {
                ...item,
                amount: "",
              };
            }

            if (
              chargedBy === "nightly" &&
              item.chargedBy === "daily"
            ) {
              return {
                ...item,
                amount: "",
              };
            }

            if (item.chargedBy === chargedBy) {
              return {
                ...item,
                amount: value,
              };
            }

            return item;
          }),
        };
      }

      // Clearing the current value simply unselects it
      return {
        ...prev,
        pricingStructures: prev.pricingStructures.map((item) =>
          item.chargedBy === chargedBy
            ? {
                ...item,
                amount: value,
              }
            : item
        ),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!form.name) {
        return toast.error("Inventory name is required");
      }

      if (!form.inventoryType) {
        return toast.error("Inventory type is required");
      }

      if (form.requiresPayment) {
        const hasAtLeastOnePricing = form.pricingStructures.some(
          (item) => item.amount
        );

        if (!hasAtLeastOnePricing) {
          return toast.error("At least one pricing structure is required");
        }
      }

      const payload = {
        name: form.name,
        description: form.description,
        category: form.category,
        inventoryType: form.inventoryType,
        totalStock:
          form.inventoryType === "non_stock"
            ? null
            : form.totalStock
            ? Number(form.totalStock)
            : null,
        lowStockThreshold:
          form.inventoryType === "non_stock"
            ? null
            : form.lowStockThreshold
            ? Number(form.lowStockThreshold)
            : null,
        remark: form.remark,
        status: form.status,
        photo: form.photo,
        pricing: form.requiresPayment
          ? form.pricingStructures
              .filter((item) => item.amount)
              .map((item) => ({
                amount: Number(item.amount),
                chargedBy: item.chargedBy,
              }))
          : [],
      };

      setLoading(true);

      await createAddOn(payload);

      toast.success("Inventory created successfully");
      navigate("/inventory");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate(-1);

  const triggerSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("inventory"))}
        title={titleCase(t("new inventory"))}
        mainTitle={titleCase(t("new inventory"))}
      />

      <Container fluid>
        <Row>
          <Col>
            <Card className="mb-4">
              <CardBody>
                <Form innerRef={formRef} onSubmit={handleSubmit}>
                  <FormGroup>
                  <Label>Photo</Label>

                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleChange("photo", e.target.files?.[0] ?? null)}
                  />

                  {form.photo && (
                    <img
                      src={URL.createObjectURL(form.photo)}
                      alt=""
                      className="img-thumbnail mt-3"
                      style={{ maxWidth: 220 }}
                    />
                  )}
                </FormGroup>
                
                  <FormGroup>
                    <Label>{titleCase(t("inventory name"))}</Label>
                    <Input
                      type="text"
                      value={form.name}
                      onChange={(e) =>
                        handleChange("name", e.target.value)
                      }
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("category"))}</Label>
                    <Input
                      type="text"
                      value={form.category}
                      onChange={(e) =>
                        handleChange("category", e.target.value)
                      }
                    />
                    <small className="text-muted d-block mt-2">
                      Add "[# pax]" to indicate additional guests allowed with purchase of item.
                    </small>
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("inventory type"))}</Label>
                    <Input
                      type="select"
                      value={form.inventoryType}
                      onChange={(e) =>
                        handleChange("inventoryType", e.target.value)
                      }
                      required
                    >
                      <option value="">
                        {titleCase(t("select type"))}
                      </option>
                      <option value="asset">
                        {titleCase(t("asset"))}
                      </option>
                      <option value="consumable">
                        {titleCase(t("consumable"))}
                      </option>
                      <option value="non_stock">
                        {titleCase(t("non stock"))}
                      </option>
                    </Input>
                  </FormGroup>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>{titleCase(t("total stock"))}</Label>
                        <Input
                          type="number"
                          min="0"
                          value={form.totalStock}
                          onChange={(e) =>
                            handleChange("totalStock", e.target.value)
                          }
                          disabled={form.inventoryType === "non_stock"}
                        />
                      </FormGroup>
                    </Col>

                    <Col md="6">
                      <FormGroup>
                        <Label>{titleCase(t("low stock threshold"))}</Label>
                        <Input
                          type="number"
                          min="0"
                          value={form.lowStockThreshold}
                          onChange={(e) =>
                            handleChange(
                              "lowStockThreshold",
                              e.target.value
                            )
                          }
                          disabled={form.inventoryType === "non_stock"}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>{titleCase(t("description"))}</Label>
                    <Input
                      type="textarea"
                      value={form.description}
                      onChange={(e) =>
                        handleChange("description", e.target.value)
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("remark"))}</Label>
                    <Input
                      type="textarea"
                      value={form.remark}
                      onChange={(e) =>
                        handleChange("remark", e.target.value)
                      }
                    />
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardHeader>
                <H5>{titleCase(t("pricing"))}</H5>
              </CardHeader>

              <CardBody>
                <Media className="mb-3 align-items-center">
                  <Label className="switch m-r-10 mb-0">
                    <Input
                      type="checkbox"
                      checked={form.requiresPayment}
                      onChange={(e) =>
                        handleChange("requiresPayment", e.target.checked)
                      }
                    />
                    <span className="switch-state"></span>
                  </Label>

                  <Label className="mb-0">
                    {titleCase(t("requires payment"))}
                  </Label>
                </Media>

                {form.requiresPayment && (
                  <>
                    <div className="mb-3">
                      <Label className="fw-bold">
                        Payment Configuration
                      </Label>

                      <small className="d-block text-muted">
                        Enter a price to select a pricing method. Uncheck a
                        selected pricing method to remove it.
                      </small>
                    </div>

                    {form.pricingStructures.map((pricing) => {
                      const selected = isPricingSelected(
                        pricing.chargedBy
                      );

                      const disabled = isPricingDisabled(
                        pricing.chargedBy
                      );

                      return (
                        <div
                          key={pricing.chargedBy}
                          className={`border rounded p-3 mb-3 ${
                            disabled ? "bg-light" : ""
                          }`}
                        >
                          <Row className="align-items-center">
                            <Col md="6">
                              <div className="checkbox checkbox-primary">
                                <Input
                                  id={`pricing-${pricing.chargedBy}`}
                                  type="checkbox"
                                  checked={selected}
                                  disabled={disabled}
                                  onChange={(e) => {
                                    if (!e.target.checked) {
                                      handlePricingChange(
                                        pricing.chargedBy,
                                        ""
                                      );
                                    }
                                  }}
                                />

                                <Label
                                  for={`pricing-${pricing.chargedBy}`}
                                  className={`mb-0 ${
                                    disabled
                                      ? "text-muted"
                                      : ""
                                  }`}
                                >
                                  <strong>
                                    {titleCase(
                                      t(
                                        pricing.chargedBy.replace(
                                          /_/g,
                                          " "
                                        )
                                      )
                                    )}
                                  </strong>
                                </Label>
                              </div>

                              {disabled && (
                                <small className="text-muted d-block mt-1 ms-4">
                                  Not available with the selected
                                  pricing method.
                                </small>
                              )}
                            </Col>

                            <Col md="6">
                              <FormGroup className="mb-0">
                                <Label>
                                  {titleCase(t("amount"))}
                                </Label>

                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  disabled={disabled}
                                  value={pricing.amount}
                                  onChange={(e) =>
                                    handlePricingChange(
                                      pricing.chargedBy,
                                      e.target.value
                                    )
                                  }
                                  placeholder="0.00"
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        </div>
                      );
                    })}
                  </>
                )}
              </CardBody>
            </Card>

            <FooterCard
              onSubmit={triggerSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default InventoryCreate;